// LAP-XXX — Social Dashboard v2 : bloc partagé "Importance + Type de relation"
// utilisé dans CreateContact / EditContact (édition) et ViewContact (lecture).
//
// Implémentation custom (sans MUI Rating ni ToggleButtonGroup) :
// - MUI Rating injecte des <input type="radio" hidden> qui peuvent rentrer en
//   conflit avec le <form> react-hook-form du parent (clics qui ne mettent pas
//   à jour le state).
// - ToggleButtonGroup a le même comportement avec ses inputs cachés.
// On utilise donc des <button type="button"> explicites (sortis du flux form)
// + stopPropagation/preventDefault pour garantir que le clic n'est jamais
// "absorbé" par un parent.
//
// Styling : couleurs neutres qui marchent en thème clair (Dialog Edit/Create
// MUI par défaut, fond blanc) et en thème sombre (Dashboard, ViewContact).

import React from 'react'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined'
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined'
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import { useTranslation } from 'react-i18next'

// Doit rester aligné avec social_dashboard_views.CANONICAL_BUCKETS côté back.
export const RELATIONSHIP_TYPES = ['family', 'pro', 'friendship', 'intimacy']

// Couleurs : cf. plan §5 (design tokens).
export const RELATIONSHIP_COLORS = {
  family: '#F59E0B',
  pro: '#3B82F6',
  friendship: '#10B981',
  intimacy: '#EC4899',
}

const ICONS = {
  family: HomeOutlinedIcon,
  pro: WorkOutlineOutlinedIcon,
  friendship: GroupOutlinedIcon,
  intimacy: FavoriteBorderOutlinedIcon,
}

const GOLD = '#F5C518'
// Empty star : suffisamment contrasté sur fond clair ET sur fond sombre.
const EMPTY = '#B0B7C3'

/**
 * Lecture seule : rang de 5 étoiles. Réutilisé en liste, fiche et dashboard.
 */
export function StarsRow({ value = 0, size = 16 }) {
  const v = Number(value || 0)
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
      {[1, 2, 3, 4, 5].map((i) => {
        const filled = i <= v
        const Icon = filled ? StarIcon : StarBorderIcon
        return (
          <Icon
            key={i}
            style={{ fontSize: size, color: filled ? GOLD : EMPTY }}
          />
        )
      })}
    </span>
  )
}

/**
 * Édition inline : rang de 5 étoiles cliquables, sauvegarde au clic via
 * POST /api/Social/contact/<id>/rating/ (cf. social_settings_views.contact_rating).
 *
 * @param {{
 *   contactId: number|string,
 *   value: number,
 *   size?: number,
 *   onSaved?: (next: { rating:number, relationship_type:string|null }) => void,
 *   stopClickPropagation?: boolean,  // utile dans une row qui ouvre la fiche
 * }} props
 */
export function StarsEditor({
  contactId,
  value = 0,
  size = 18,
  onSaved,
  stopClickPropagation = true,
}) {
  const [current, setCurrent] = React.useState(Number(value || 0))
  const [saving, setSaving] = React.useState(false)

  React.useEffect(() => {
    setCurrent(Number(value || 0))
  }, [value])

  const onPick = async (e, next) => {
    if (stopClickPropagation) {
      e.preventDefault()
      e.stopPropagation()
    }
    if (!contactId) {
      // eslint-disable-next-line no-console
      console.warn('[StarsEditor] contactId manquant — clic ignoré')
      return
    }
    const previous = current
    setCurrent(next) // optimistic
    setSaving(true)
    try {
      // Import paresseux pour éviter un cycle entre _shared et utils.
      const { makePostRequest } = await import('utils/api')
      const url = `/api/Social/contact/${contactId}/rating/`
      const res = await makePostRequest(
        url,
        { rating: next },
        { headers: { 'Content-Type': 'application/json' } },
        true,
      )
      const ok =
        res && typeof res.status === 'number' && res.status >= 200 && res.status < 300
      if (ok) {
        if (onSaved) onSaved(res.data)
      } else {
        setCurrent(previous) // rollback
        // eslint-disable-next-line no-console
        console.error(
          `[StarsEditor] save KO : POST ${url} -> ${res?.status ?? 'no response'}`,
          res?.data,
        )
        if (typeof window !== 'undefined') {
          // Affiche un toast simple via alert() pour que l'utilisateur voie
          // l'erreur sans avoir à ouvrir la console.
          const detail =
            res?.data?.detail ||
            (res?.status === 404
              ? "L'endpoint n'existe pas encore — redémarre le serveur Django."
              : res?.status === 401 || res?.status === 403
              ? 'Non authentifié — reconnecte-toi.'
              : `Erreur HTTP ${res?.status ?? 'inconnue'}`)
          window.alert(`Impossible d'enregistrer le rating : ${detail}`)
        }
      }
    } catch (err) {
      setCurrent(previous)
      // eslint-disable-next-line no-console
      console.error('[StarsEditor] save exception', err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        opacity: saving ? 0.6 : 1,
      }}
    >
      {[1, 2, 3, 4, 5].map((i) => {
        const filled = i <= current
        const Icon = filled ? StarIcon : StarBorderIcon
        // Toggle : recliquer sur l'étoile sélectionnée la décoche.
        const next = current === i ? i - 1 : i
        return (
          <button
            key={i}
            type="button"
            onMouseDown={
              stopClickPropagation
                ? (e) => {
                    e.preventDefault()
                    e.stopPropagation()
                  }
                : undefined
            }
            onClick={(e) => onPick(e, Math.max(0, Math.min(5, next)))}
            disabled={saving}
            aria-label={`${i} ${i > 1 ? 'étoiles' : 'étoile'}`}
            style={{
              background: 'transparent',
              border: 'none',
              padding: 1,
              cursor: saving ? 'wait' : 'pointer',
              lineHeight: 0,
              color: filled ? GOLD : EMPTY,
            }}
          >
            <Icon style={{ fontSize: size }} />
          </button>
        )
      })}
    </span>
  )
}

function StarButton({ index, value, onChange, size = 28 }) {
  const filled = index <= value
  const Icon = filled ? StarIcon : StarBorderIcon
  const stop = (e) => {
    // Empêche le <form> parent de capter le click ou de submit.
    e.preventDefault()
    e.stopPropagation()
  }
  const handleClick = (e) => {
    stop(e)
    // Toggle : recliquer sur l'étoile sélectionnée la décoche → revient à index-1.
    const next = value === index ? index - 1 : index
    onChange(Math.max(0, Math.min(5, next)))
  }
  return (
    <button
      type="button"
      onClick={handleClick}
      onMouseDown={stop}
      aria-label={`${index} ${index > 1 ? 'étoiles' : 'étoile'}`}
      style={{
        background: 'transparent',
        border: 'none',
        padding: 2,
        cursor: 'pointer',
        lineHeight: 0,
        color: filled ? GOLD : EMPTY,
      }}
    >
      <Icon style={{ fontSize: size }} />
    </button>
  )
}

function TypeChip({ typeKey, selected, onChange, label }) {
  const color = RELATIONSHIP_COLORS[typeKey]
  const Icon = ICONS[typeKey]
  const stop = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }
  const handleClick = (e) => {
    stop(e)
    onChange(selected ? null : typeKey)
  }
  return (
    <button
      type="button"
      onClick={handleClick}
      onMouseDown={stop}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 12px',
        borderRadius: 999,
        border: selected ? 'none' : `1px solid ${color}55`,
        backgroundColor: selected ? color : 'transparent',
        color: selected ? '#fff' : color,
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
        lineHeight: 1,
      }}
    >
      <Icon style={{ fontSize: 16 }} />
      <span>{label}</span>
    </button>
  )
}

/**
 * Bloc Rating + chips type-de-relation, utilisé en édition.
 * @param {{
 *   rating: number,
 *   relationshipType: string|null,
 *   onChangeRating: (n: number) => void,
 *   onChangeRelationshipType: (t: string|null) => void,
 * }} props
 */
export default function RelationshipFields({
  rating,
  relationshipType,
  onChangeRating,
  onChangeRelationshipType,
}) {
  const { t } = useTranslation('layout')
  const value = Number(rating || 0)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, margin: '12px 0' }}>
      <div>
        <div
          style={{
            fontSize: 12,
            color: '#7a829a',
            marginBottom: 4,
            fontWeight: 500,
          }}
        >
          {t('social.contact.rating.label', 'Importance')}
        </div>
        <div style={{ display: 'inline-flex' }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <StarButton
              key={i}
              index={i}
              value={value}
              onChange={onChangeRating}
            />
          ))}
        </div>
      </div>

      <div>
        <div
          style={{
            fontSize: 12,
            color: '#7a829a',
            marginBottom: 6,
            fontWeight: 500,
          }}
        >
          {t('social.contact.relationshipType.label', 'Type de relation')}
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {RELATIONSHIP_TYPES.map((typeKey) => (
            <TypeChip
              key={typeKey}
              typeKey={typeKey}
              selected={relationshipType === typeKey}
              onChange={onChangeRelationshipType}
              label={t(`social.contact.relationshipType.${typeKey}`, typeKey)}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
